<template>
  <div
    class="accomodationCard"
    :class="{
      parking: isParking,
      'optional-services': optionalServices && optionalServices.length
    }"
  >
    <div class="accomodationCard__header">
      <h4>
        <div>
          <md-icon v-if="isParking" name="directions_car" />
          <md-icon v-else name="home" />
          {{ offer.unit_type.commercial_title }}
          <el-popover
            v-if="
              offer.unit_type.description &&
                offer.unit_type.description !== 'n/c'
            "
            trigger="click"
            :content="offer.unit_type.description"
          >
            <el-button slot="reference">
              <md-icon name="info" />
            </el-button>
          </el-popover>
        </div>
        <div class="roomMates">
          <template v-if="offer.unit_type.roomates_count > 1">
            <md-icon name="people" />
            {{ $t('residences.accomodation.sharedRoomAvailable') }}
          </template>
          <template v-if="offer.unit_type.roomates_count === 1">
            <md-icon name="person" />
            {{ $t('residences.accomodation.sharedRoomNotAvailable') }}
          </template>
        </div>
      </h4>
      <span
        v-if="availabilitySelected"
        class="accomodationCard__header-subtitle"
      >
        {{ availabilitySelected.quantity }}
        {{ $t('residences.accomodation.available')
        }}{{
          $i18n.locale === 'fr' && availabilitySelected.quantity > 1 ? 's' : ''
        }}
      </span>
    </div>
    <div class="accomodationCard__body">
      <template v-if="seeMore === false">
        <div>
          <div class="accomodationCard__body-line">
            <div class="rent">
              <b>{{ $t('residences.accomodation.rent') }}</b>
              <rent-display :offer="offer" />
            </div>
          </div>
          <div class="accomodationCard__body-line">
            <div class="warranty">
              <b>{{ $t('residences.accomodation.warranty') }}</b>
              <deposit-display :offer="offer" />
            </div>
          </div>
        </div>
        <div>
          <div v-if="!isParking" class="accomodationCard__body-line">
            <div class="size">
              <b>{{ $t('residences.accomodation.size') }}</b>
              <surface-display :offer="offer" />
            </div>
          </div>
          <div
            v-if="!isParking && offer.offer_pricing.packages_amount_to"
            class="accomodationCard__body-line"
          >
            <div class="entryPrice">
              <b>{{ $t('residences.accomodation.entryPrice') }}</b>
              <packages-display :offer="offer" />
            </div>
          </div>
        </div>
        <div>
          <div
            v-if="!isParking && optionalServices && optionalServices.length"
            class="accomodationCard__body-line"
          >
            <div class="optionalServices">
              <b>{{ $t('residences.accomodation.optionalServices') }}</b>
              <ul class="accomodationCard__optional_services">
                <li v-for="service in optionalServices" :key="service.id">
                  <span>
                    {{ service.title }}
                  </span>
                  <el-popover
                    v-if="service.description"
                    trigger="click"
                    width="250"
                    :content="service.description"
                  >
                    <el-button slot="reference">
                      <md-icon name="info" />
                    </el-button>
                  </el-popover>
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div class="accomodationCard__equipped">
          <div class="accomodationCard__body-line">
            <div
              :class="[
                'equiped',
                offer.unit_type.equipped ? 'active' : 'inactive'
              ]"
            >
              <el-popover
                ref="popover"
                trigger="hover"
                width="250"
                :content="offer.optional_comment_equipped"
              >
              </el-popover>
              <div v-popover:popover>
                {{ $t('residences.accomodation.equiped') }}
                <md-icon
                  :name="
                    offer.unit_type.equipped ? 'check_circle' : 'highlight_off'
                  "
                />
              </div>
            </div>
          </div>
        </div>
      </template>
      <template v-else>
        <div class="accomodationCard__body-line">
          {{ offer.optional_comment_equipped }}
        </div>
      </template>
    </div>
    <div class="accomodationCard__actions">
      <div v-if="!isParking" class="seeMore" @click="seeMore = !seeMore">
        <span
          >{{ $t('residences.accomodation.see') }}
          {{
            seeMore
              ? $t('residences.accomodation.less')
              : $t('residences.accomodation.more')
          }}</span
        >
        <md-icon name="chevron_right" />
      </div>
      <el-button
        type="primary"
        :loading="loading"
        @click="selectAccomodation()"
        >{{ $t('residences.accomodation.book') }}</el-button
      >
    </div>
    <reserved-dialog
      v-if="dialogVisible"
      :visible="dialogVisible"
      :offer="offer"
      @close="closeDialog"
      @access="pinDialogVisible = true"
    />
    <reserved-pin-dialog
      v-if="pinDialogVisible"
      :visible="pinDialogVisible"
      :invalid="pinDialogInvalid"
      @close="closePinDialog"
      @verification="handleVerification"
    />
  </div>
</template>

<script>
import ReservedDialog from '@/components/residences/ReservedDialog'
import ReservedPinDialog from '@/components/residences/ReservedPinDialog'
import DepositDisplay from '@/components/ui/DepositDisplay'
import PackagesDisplay from '@/components/ui/PackagesDisplay'
import RentDisplay from '@/components/ui/RentDisplay'
import SurfaceDisplay from '@/components/ui/SurfaceDisplay'
import { mapState } from 'vuex'

export default {
  name: 'AccomodationCard',
  components: {
    SurfaceDisplay,
    DepositDisplay,
    RentDisplay,
    PackagesDisplay,
    ReservedDialog,
    ReservedPinDialog
  },
  props: {
    offer: {
      type: Object,
      required: true
    },
    selectedMonth: {
      type: String,
      required: true
    },
    residence: {
      type: Object,
      default: null
    }
  },
  data: () => ({
    seeMore: false,
    loading: false,
    dialogVisible: false,
    pinDialogVisible: false,
    pinDialogInvalid: false
  }),
  computed: {
    ...mapState({
      selectedAvailability: state => state.residences.selectedAvailability
    }),
    availabilitySelected() {
      let parsedDate = this.selectedMonth.split('-').map(i => parseInt(i))

      return this.offer.availabilities.filter(availability => {
        return (
          availability.year === parsedDate[0] &&
          availability.month === parsedDate[1]
        )
      })[0]
    },
    optionalServices() {
      return this.residence.preview.residence_services
    },
    isParking() {
      return (
        this.offer.unit_type.kind === 'PKI' ||
        this.offer.unit_type.kind === 'PKE'
      )
    }
  },
  methods: {
    async selectAccomodation() {
      if (this.offer.booking_restriction.enabled) {
        // reserved residence
        this.pinDialogInvalid = false
        this.dialogVisible = true
      } else {
        // no reserved, do select normally
        this.doSelectAccomodation()
      }
    },
    async doSelectAccomodation(code) {
      this.loading = true
      this.$store.dispatch('residences/setSelectedAccomodation', {
        ...this.offer
      })
      this.$store.dispatch('residences/setSelectedAvailability', {
        ...this.availabilitySelected
      })

      if (this.$store.getters['auth/logged_in'] && this.selectedAvailability) {
        try {
          await this.$store.dispatch('records/createRecord', {
            residence: this.residence,
            availability: this.selectedAvailability,
            offer_booking_restriction_code: code
          })

          this.$notify({
            type: 'success',
            title: this.$t('base.success.notificationTitle'),
            message: this.$t('residences.accomodation.folderCreated')
          })
        } catch (error) {
          console.warn(error)
        }
      } else {
        this.$router.push({
          name: 'register',
          params: {
            reservation: true,
            code // send code also to register action
          }
        })
      }
      this.loading = false
    },
    closeDialog() {
      this.dialogVisible = false
      this.pinDialogVisible = false
    },
    closePinDialog() {
      this.pinDialogVisible = false
    },
    async handleVerification(code) {
      // verification of code
      try {
        const response = await this.$api.residences.getBookingRestrictionCode({
          id: this.residence.id,
          code,
          offerId: this.offer.id
        })
        if (response.found) {
          // if code is valid
          this.doSelectAccomodation(code)
          this.pinDialogInvalid = false
          this.closeDialog()
        } else {
          // if code is invalid
          this.pinDialogInvalid = true
        }
      } catch (error) {
        // if code is invalid
        this.pinDialogInvalid = true
      }
    }
  }
}
</script>

<style lang="scss" scoped>
$c: '.accomodationCard';

#{$c} {
  position: relative;
  height: 360px;
  border: solid 1px #dcdfe6;
  border-radius: 8px;
  overflow: hidden;

  @include respond-above(lg) {
    display: flex;
    margin-bottom: 5px;
    height: 100%;
  }

  &.optional-services {
    height: 420px;

    @include respond-above(lg) {
      height: 100%;
    }
  }

  &.parking {
    #{$c}__header {
      background-color: #303133;
    }
  }

  #{$c}__header {
    height: 85px;
    background-color: #303133;
    color: #ffffff;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding-left: 26px;
    padding-right: 13px;

    @include respond-above(lg) {
      height: 100%;
      width: 365px;
    }

    h4 {
      display: flex;
      align-items: baseline;
      justify-content: space-between;
      position: relative;
      width: 100%;

      i {
        vertical-align: bottom;
      }

      .roomMates {
        i {
          vertical-align: bottom;
          margin-left: 10px;
        }
      }
    }

    &-subtitle {
      font-size: 16px;
      font-weight: normal;
      padding-top: 5px;
      padding-left: 30px;
    }

    .el-button {
      border: 0;
      padding: 1px;
      background: none;
      color: #f4b452;
    }
    .info {
      font-size: 14px;
    }
  }

  #{$c}__body {
    position: relative;
    padding: 24px 16px;

    @include respond-above(lg) {
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;
    }

    & > div {
      margin: 0 5px;
    }

    &-line {
      display: flex;
      justify-content: space-between;
      margin-top: 12px;
      margin-bottom: 12px;

      &:first-child {
        margin-top: 0;
      }

      .equiped {
        text-align: right;
        width: 100%;

        i {
          font-size: 17px;
          margin-left: 8px;
        }
        &.active i {
          color: $--color-success;
        }
        &.inactive i {
          color: $--color-danger;
        }
      }
    }
  }

  #{$c}__actions {
    position: absolute;
    bottom: 24px;
    left: 24px;
    width: calc(100% - 24px);
    display: flex;
    align-items: center;
    justify-content: center;

    @include respond-above(lg) {
      position: relative;
      bottom: auto;
      left: auto;
      width: auto;
      margin: 0 10px;
    }

    .seeMore {
      position: absolute;
      right: 24px;
      top: -36px;
      display: flex;
      align-items: center;
      cursor: pointer;
      user-select: none;

      span {
        text-decoration: underline;
      }

      @include respond-above(lg) {
        display: none;
      }
    }
  }

  &__optional_services {
    margin: 0;
    padding-left: 20px;

    .el-button {
      border: 0;
      padding: 1px;
    }
    .info {
      font-size: 14px;
    }
  }

  &__equipped {
    @include respond-below(lg) {
      position: absolute;
      top: 0;
      width: calc(100% - 48px);
      padding-top: 2px;
    }
  }
}
</style>
